import NavigateBefore from "@material-ui/icons/NavigateBefore";
import NavigateNext from "@material-ui/icons/NavigateNext";
import React, { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Close, Search, Business } from "@material-ui/icons";

export default function SelectModal({
	title,
	items,
	selected,
	onSelect,
	onClose,
}) {
	const [currentPage, setCurrentPage] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const itemsPerPage = 8;

	const filteredItems = useMemo(() => {
		if (!searchTerm.trim()) return items;
		const term = searchTerm.toLowerCase();
		return items.filter(item =>
			item.label?.toLowerCase().includes(term)
		);
	}, [items, searchTerm]);

	const pages = useMemo(() => {
		const result = [];
		for (let i = 0; i < filteredItems.length; i += itemsPerPage) {
			result.push(filteredItems.slice(i, i + itemsPerPage));
		}
		return result.length > 0 ? result : [[]];
	}, [filteredItems]);

	const totalPages = pages.length;

	const handleSearch = (value) => {
		setSearchTerm(value);
		setCurrentPage(0);
	};

	const handlePrevious = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (currentPage > 0) {
			setCurrentPage(currentPage - 1);
		}
	};

	const handleNext = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (currentPage < totalPages - 1) {
			setCurrentPage(currentPage + 1);
		}
	};

	const handleClose = (e) => {
		e.preventDefault();
		e.stopPropagation();
		onClose();
	};

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleItemSelect = (item) => {
		onSelect(item);
	};

	return (
		<Overlay onClick={handleOverlayClick}>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<HeaderIcon>
						<Business />
					</HeaderIcon>
					<Title>{title}</Title>
					<CloseButton onClick={handleClose}>
						<Close />
					</CloseButton>
				</ModalHeader>

				<ModalBody>
					<SearchContainer>
						<SearchIcon>
							<Search />
						</SearchIcon>
						<SearchInput
							type="text"
							placeholder="Buscar empresa..."
							value={searchTerm}
							onChange={(e) => handleSearch(e.target.value)}
							autoFocus
						/>
						{searchTerm && (
							<ClearButton onClick={() => handleSearch("")}>
								<Close style={{ fontSize: '16px' }} />
							</ClearButton>
						)}
					</SearchContainer>

					<InfoBar>
						<ResultCount>
							{filteredItems.length} {filteredItems.length === 1 ? 'empresa' : 'empresas'}
							{searchTerm && ` de ${items.length}`}
						</ResultCount>
						<NavigationControls>
							<NavButton
								disabled={currentPage === 0}
								onClick={handlePrevious}
								title="Anterior"
							>
								<NavigateBefore />
							</NavButton>
							<PageIndicator>
								{totalPages > 0 ? `${currentPage + 1} de ${totalPages}` : '-'}
							</PageIndicator>
							<NavButton
								disabled={currentPage >= totalPages - 1}
								onClick={handleNext}
								title="Próximo"
							>
								<NavigateNext />
							</NavButton>
						</NavigationControls>
					</InfoBar>

					<List>
						{pages[currentPage]?.length > 0 ? (
							pages[currentPage].map((item, index) => (
								<Item
									key={`${item.value}-${index}`}
									selected={selected === item.value}
									onClick={() => handleItemSelect(item)}
								>
									<ItemIcon selected={selected === item.value}>
										<Business style={{ fontSize: '18px' }} />
									</ItemIcon>
									<ItemLabel>{item.label}</ItemLabel>
								</Item>
							))
						) : (
							<EmptyState>
								<Search style={{ fontSize: '40px', color: '#cbd5e1', marginBottom: '12px' }} />
								<EmptyText>Nenhuma empresa encontrada</EmptyText>
								{searchTerm && <EmptySubtext>Tente outro termo de busca</EmptySubtext>}
							</EmptyState>
						)}
					</List>
				</ModalBody>

				<ModalFooter>
					<FooterButton variant="secondary" onClick={handleClose}>
						Cancelar
					</FooterButton>
				</ModalFooter>
			</ModalContent>
		</Overlay>
	);
}

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const slideUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(20px) scale(0.98);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(15, 23, 42, 0.7);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 2000;
	animation: ${fadeIn} 0.2s ease;
	backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	background-color: #fff;
	border-radius: 20px;
	width: 480px;
	max-width: 90vw;
	max-height: 85vh;
	box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
	animation: ${slideUp} 0.3s ease;
	overflow: hidden;
	font-family: 'Mulish', sans-serif;
`;

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 20px 24px;
	background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
	color: white;
`;

const HeaderIcon = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.2);

	svg {
		font-size: 22px;
	}
`;

const Title = styled.h2`
	flex: 1;
	font-size: 18px;
	font-weight: 700;
	margin: 0;
	font-family: 'Mulish', sans-serif;
`;

const CloseButton = styled.button`
	background: rgba(255, 255, 255, 0.15);
	border: none;
	border-radius: 8px;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.25);
		transform: rotate(90deg);
	}

	svg {
		font-size: 20px;
	}
`;

const ModalBody = styled.div`
	display: flex;
	flex-direction: column;
	padding: 20px;
	flex: 1;
	overflow: hidden;
`;

const SearchContainer = styled.div`
	display: flex;
	align-items: center;
	position: relative;
	margin-bottom: 16px;
`;

const SearchIcon = styled.div`
	position: absolute;
	left: 14px;
	color: #9ca3af;
	display: flex;
	align-items: center;
	pointer-events: none;

	svg {
		font-size: 20px;
	}
`;

const SearchInput = styled.input`
	width: 100%;
	padding: 14px 44px;
	border: 2px solid #e5e7eb;
	border-radius: 12px;
	font-size: 15px;
	font-family: 'Mulish', sans-serif;
	transition: all 0.2s ease;
	background: #f9fafb;

	&:focus {
		outline: none;
		border-color: #8e44ad;
		box-shadow: 0 0 0 4px rgba(142, 68, 173, 0.1);
		background: white;
	}

	&::placeholder {
		color: #9ca3af;
	}
`;

const ClearButton = styled.button`
	position: absolute;
	right: 12px;
	width: 24px;
	height: 24px;
	border: none;
	border-radius: 50%;
	background: #e5e7eb;
	color: #6b7280;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;

	&:hover {
		background: #d1d5db;
		color: #374151;
	}
`;

const InfoBar = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background: #f8f9fa;
	border-radius: 10px;
	margin-bottom: 12px;
`;

const ResultCount = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	font-family: 'Mulish', sans-serif;
`;

const NavigationControls = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const NavButton = styled.button`
	background: ${props => props.disabled ? '#e5e7eb' : 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)'};
	padding: 6px;
	border: 0;
	border-radius: 8px;
	cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
	color: ${props => props.disabled ? '#9ca3af' : '#fff'};
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	box-shadow: ${props => props.disabled ? 'none' : '0 2px 4px rgba(142, 68, 173, 0.3)'};

	&:hover:not(:disabled) {
		transform: translateY(-1px);
		box-shadow: 0 4px 8px rgba(142, 68, 173, 0.4);
	}

	&:active:not(:disabled) {
		transform: translateY(0);
	}

	svg {
		font-size: 20px;
	}
`;

const PageIndicator = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: #4b5563;
	min-width: 56px;
	text-align: center;
	font-family: 'Mulish', sans-serif;
`;

const List = styled.div`
	flex: 1;
	overflow-y: auto;
	min-height: 200px;
	max-height: 340px;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: #d1d5db;
		border-radius: 10px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: #9ca3af;
	}
`;

const Item = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 16px;
	cursor: pointer;
	border-radius: 10px;
	margin-bottom: 6px;
	font-size: 14px;
	font-family: 'Mulish', sans-serif;
	transition: all 0.2s ease;
	background: ${props => props.selected ? 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)' : 'white'};
	color: ${props => props.selected ? '#fff' : '#2c3e50'};
	border: 1px solid ${props => props.selected ? 'transparent' : '#e5e7eb'};
	box-shadow: ${props => props.selected ? '0 4px 12px rgba(142, 68, 173, 0.3)' : 'none'};

	&:hover {
		background: ${props => props.selected ? 'linear-gradient(135deg, #7d3c98 0%, #8e44ad 100%)' : '#f3f4f6'};
		border-color: ${props => props.selected ? 'transparent' : '#d1d5db'};
		transform: translateX(4px);
	}

	&:last-child {
		margin-bottom: 0;
	}
`;

const ItemIcon = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.selected ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6'};
	color: ${props => props.selected ? 'white' : '#8e44ad'};
	flex-shrink: 0;
`;

const ItemLabel = styled.span`
	flex: 1;
	font-weight: 500;
	line-height: 1.4;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 48px 24px;
	text-align: center;
`;

const EmptyText = styled.p`
	font-size: 15px;
	font-weight: 600;
	color: #4b5563;
	margin: 0 0 4px 0;
	font-family: 'Mulish', sans-serif;
`;

const EmptySubtext = styled.p`
	font-size: 13px;
	color: #9ca3af;
	margin: 0;
	font-family: 'Mulish', sans-serif;
`;

const ModalFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 16px 20px;
	background: #f8f9fa;
	border-top: 1px solid #e5e7eb;
`;

const FooterButton = styled.button`
	background: ${props => props.variant === 'secondary' ? 'white' : 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)'};
	color: ${props => props.variant === 'secondary' ? '#6b7280' : 'white'};
	border: ${props => props.variant === 'secondary' ? '2px solid #e5e7eb' : 'none'};
	border-radius: 10px;
	padding: 12px 28px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	font-family: 'Mulish', sans-serif;

	&:hover {
		background: ${props => props.variant === 'secondary' ? '#f3f4f6' : 'linear-gradient(135deg, #7d3c98 0%, #8e44ad 100%)'};
		border-color: ${props => props.variant === 'secondary' ? '#d1d5db' : 'transparent'};
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;
